/**
 * Recording Session Component
 *
 * Main recording interface with camera preview, pose detection, and controls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Chip, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle, Warning, Error as ErrorIcon, CameraAlt, CloudQueue } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import CameraPreview from './components/CameraPreview';
import RecordingControls from './components/RecordingControls';
import PoseOverlay from './components/PoseOverlay';
import { useRecording } from '../../../../context/RecordingContext';
import { RecordingActionType } from '../../../../context/RecordingContext';
import { VideoRecorder } from '../../../../common/recordingUtilities';
import MediaPipeManager, { getOptimalDetectionInterval } from '../../../../common/mediaPipeUtilities';
import { analyzePose, MessageDebouncer, QualitySmoother } from '../../../../common/poseAnalysisUtilities';
import { DeviceOrientationManager, getFilmingGuidance } from '../../../../common/deviceOrientationUtilities';
import { debugLog } from '../../../../common/debugUtilities';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(3),
    maxWidth: 800,
    margin: '0 auto'
  },
  feedbackContainer: {
    marginBottom: theme.spacing(2),
    minHeight: 60,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  feedbackChip: {
    fontSize: '0.95rem',
    padding: theme.spacing(1, 2)
  },
  detectionStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  }
}));

interface RecordingSessionProps {
  infantAgeMonths?: number;
  onRecordingComplete: (blob: Blob) => void;
  onCancel: () => void;
}

export default function RecordingSession({
  infantAgeMonths,
  onRecordingComplete,
  onCancel
}: RecordingSessionProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();
  const { state, dispatch } = useRecording();

  const [videoRecorder] = useState(() => new VideoRecorder());
  const [mediaPipeManager, setMediaPipeManager] = useState<MediaPipeManager | null>(null);
  const [orientationManager] = useState(() => new DeviceOrientationManager());
  const [isMediaPipeLoading, setMediaPipeLoading] = useState(true);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const detectionLoopRef = useRef<number | null>(null);
  const lastDetectionTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<number | null>(null);

  // Phase 2: Advanced analysis with debouncing and smoothing
  const messageDebouncer = useRef(new MessageDebouncer(1000)); // 1 second debounce
  const qualitySmoother = useRef(new QualitySmoother(5)); // Average over 5 frames
  const [cameraSize, setCameraSize] = useState({ width: 640, height: 480 });

  const detectionInterval = getOptimalDetectionInterval();

  // Initialize MediaPipe
  useEffect(() => {
    let mounted = true;

    async function initMediaPipe() {
      try {
        setMediaPipeLoading(true);
        debugLog('[RecordingSession] Initializing MediaPipe...');
        const manager = await MediaPipeManager.getInstance();
        if (mounted) {
          debugLog('[RecordingSession] MediaPipe initialized successfully');
          setMediaPipeManager(manager);
          setMediaPipeLoading(false);
          dispatch({ type: RecordingActionType.SET_POSE_DETECTION_ACTIVE, payload: true });
        }
      } catch (error) {
        console.error('[RecordingSession] MediaPipe initialization failed:', error);
        if (mounted) {
          setMediaPipeLoading(false);
          dispatch({
            type: RecordingActionType.SET_POSE_DETECTION_ERROR,
            payload: t('errors.mediapipefailed', {ns: 'guidedrecording'})
          });
        }
      }
    }

    // Start MediaPipe initialization in background (non-blocking)
    initMediaPipe();

    return () => {
      mounted = false;
    };
  }, [dispatch, t]);

  // Initialize device orientation tracking
  useEffect(() => {
    let mounted = true;

    async function initOrientation() {
      if (!orientationManager.isSupported()) {
        debugLog('[RecordingSession] Device orientation not supported (desktop browser)');
        return;
      }

      try {
        debugLog('[RecordingSession] Requesting device orientation permission...');
        const permissionGranted = await orientationManager.requestPermission();

        if (!permissionGranted) {
          console.warn('[RecordingSession] Device orientation permission denied');
          return;
        }

        if (mounted) {
          debugLog('[RecordingSession] Starting device orientation tracking');
          orientationManager.startListening((data) => {
            // Update device orientation state
            dispatch({
              type: RecordingActionType.UPDATE_DEVICE_ORIENTATION,
              payload: {
                beta: data.beta,
                filmingAngle: data.filmingAngle,
                isLandscape: data.isLandscape
              }
            });

            // Log orientation changes
            debugLog('[RecordingSession] Device orientation:', {
              beta: data.beta,
              angle: data.filmingAngle,
              landscape: data.isLandscape
            });
          });
        }
      } catch (error) {
        console.error('[RecordingSession] Device orientation initialization failed:', error);
      }
    }

    initOrientation();

    return () => {
      mounted = false;
      orientationManager.stopListening();
    };
  }, [dispatch, orientationManager]);

  // Pose detection loop
  const runPoseDetection = useCallback(
    async (timestamp: number) => {
      if (!videoElementRef.current || !mediaPipeManager) {
        return;
      }

      // Throttle detection
      if (timestamp - lastDetectionTimeRef.current < detectionInterval) {
        detectionLoopRef.current = requestAnimationFrame(runPoseDetection);
        return;
      }

      try {
        const result = await mediaPipeManager.detectPose(videoElementRef.current, timestamp);

        if (result && result.landmarks && result.landmarks.length > 0) {
          debugLog('[RecordingSession] Baby detected, landmarks:', result.landmarks.length);
          dispatch({ type: RecordingActionType.SET_INFANT_DETECTED, payload: true });
          dispatch({ type: RecordingActionType.UPDATE_CURRENT_POSE, payload: result });

          // Phase 2: Advanced pose analysis
          const landmarks = result.landmarks[0];
          const analysis = analyzePose(landmarks);

          // Smooth the framing quality to reduce jitter
          const smoothedQuality = qualitySmoother.current.add(analysis.framingQuality);

          // Get the primary message (highest priority)
          const primaryMessage = analysis.messages[0];

          // Only update UI if message has persisted for debounce time
          if (primaryMessage && messageDebouncer.current.shouldUpdate(primaryMessage.message)) {
            debugLog('[RecordingSession] Updating guidance:', primaryMessage.message, 'Orientation:', analysis.orientation);
            dispatch({ type: RecordingActionType.SET_FRAMING_QUALITY, payload: smoothedQuality });
            dispatch({ type: RecordingActionType.UPDATE_GUIDANCE, payload: analysis.messages });
          }

          // Update detected orientation
          dispatch({ type: RecordingActionType.SET_INFANT_ORIENTATION, payload: analysis.orientation });
        } else {
          debugLog('[RecordingSession] No baby detected');
          dispatch({ type: RecordingActionType.SET_INFANT_DETECTED, payload: false });
          dispatch({ type: RecordingActionType.SET_FRAMING_QUALITY, payload: 'poor' });
          dispatch({
            type: RecordingActionType.UPDATE_GUIDANCE,
            payload: [{
              type: 'warning',
              message: t('session.notdetected', {ns: 'guidedrecording'}),
              priority: 1
            }]
          });
        }

        lastDetectionTimeRef.current = timestamp;
      } catch (error) {
        console.error('[RecordingSession] Pose detection error:', error);
      }

      detectionLoopRef.current = requestAnimationFrame(runPoseDetection);
    },
    [mediaPipeManager, detectionInterval, dispatch, t]
  );

  // Start detection when MediaPipe becomes ready (if video already ready)
  useEffect(() => {
    if (mediaPipeManager && mediaPipeManager.isReady() && videoElementRef.current && !detectionLoopRef.current) {
      debugLog('[RecordingSession] MediaPipe ready, video already loaded, starting detection');
      detectionLoopRef.current = requestAnimationFrame(runPoseDetection);
    }
  }, [mediaPipeManager, runPoseDetection]);

  // Start pose detection when video is ready
  const handleVideoReady = useCallback(
    (videoElement: HTMLVideoElement) => {
      debugLog('[RecordingSession] Video ready, starting pose detection');
      debugLog('[RecordingSession] MediaPipe ready:', mediaPipeManager?.isReady());
      videoElementRef.current = videoElement;

      // Track actual displayed video dimensions for overlay
      const updateDimensions = () => {
        const newSize = {
          width: videoElement.clientWidth || 640,
          height: videoElement.clientHeight || 480
        };
        debugLog('[RecordingSession] Updating camera size:', newSize);
        setCameraSize(newSize);
      };
      updateDimensions();

      if (mediaPipeManager && mediaPipeManager.isReady()) {
        debugLog('[RecordingSession] Starting detection loop');
        detectionLoopRef.current = requestAnimationFrame(runPoseDetection);
      } else {
        debugLog('[RecordingSession] MediaPipe not ready yet, detection will start when ready');
      }
    },
    [mediaPipeManager, runPoseDetection]
  );

  // Start recording
  const handleStartRecording = useCallback(() => {
    if (!state.cameraStream) {
      console.error('[RecordingSession] No camera stream available');
      return;
    }

    try {
      videoRecorder.startRecording(state.cameraStream, (blob) => {
        dispatch({ type: RecordingActionType.SET_RECORDED_BLOB, payload: blob });
        onRecordingComplete(blob);
      });

      dispatch({ type: RecordingActionType.START_RECORDING });

      // Update duration every second
      durationIntervalRef.current = window.setInterval(() => {
        const duration = videoRecorder.getRecordingDuration();
        dispatch({ type: RecordingActionType.UPDATE_RECORDING_DURATION, payload: duration });
      }, 1000);
    } catch (error) {
      console.error('[RecordingSession] Failed to start recording:', error);
      dispatch({
        type: RecordingActionType.SET_ERROR,
        payload: t('errors.recordingfailed', {ns: 'guidedrecording'})
      });
    }
  }, [state.cameraStream, videoRecorder, dispatch, onRecordingComplete, t]);

  // Stop recording
  const handleStopRecording = useCallback(() => {
    videoRecorder.stopRecording();
    dispatch({ type: RecordingActionType.STOP_RECORDING });

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, [videoRecorder, dispatch]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (detectionLoopRef.current) {
        cancelAnimationFrame(detectionLoopRef.current);
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  // Get feedback icon
  const getFeedbackIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle style={{ color: '#4caf50' }} />;
      case 'warning':
        return <Warning style={{ color: '#ff9800' }} />;
      case 'error':
        return <ErrorIcon style={{ color: '#f44336' }} />;
      default:
        return undefined;
    }
  };

  // Debug logging for state
  useEffect(() => {
    debugLog('[RecordingSession] State update:', {
      infantDetected: state.infantDetected,
      orientation: state.infantOrientation,
      guidanceCount: state.currentGuidance.length,
      guidanceMessage: state.currentGuidance[0]?.message
    });
  }, [state.infantDetected, state.infantOrientation, state.currentGuidance]);

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper}>
        {/* Camera Preview with Overlays */}
        <Box style={{
          position: 'relative',
          maxWidth: 640,
          margin: '0 auto',
          maxHeight: 'calc(100vh - 200px)', // Ensure it fits in viewport with room for controls
          display: 'flex',
          flexDirection: 'column'
        }}>
          <CameraPreview
            stream={state.cameraStream}
            onVideoReady={handleVideoReady}
          />

          {/* Pose Skeleton Overlay - Only show when baby is detected */}
          {state.infantDetected && state.currentPose?.landmarks && state.currentPose.landmarks.length > 0 && (
            <PoseOverlay
              landmarks={state.currentPose.landmarks[0]}
              width={cameraSize.width}
              height={cameraSize.height}
              showSkeleton={true}
              showKeypoints={true}
            />
          )}

          {/* Baby Detection & Orientation Badge - Top Left */}
          {state.infantDetected && (
            <Box
              style={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 20,
                display: 'flex',
                gap: 8
              }}
            >
              <Chip
                icon={<CheckCircle style={{ color: '#fff' }} />}
                label={state.infantOrientation !== 'unknown'
                  ? state.infantOrientation.charAt(0).toUpperCase() + state.infantOrientation.slice(1)
                  : 'Detected'}
                style={{
                  backgroundColor: state.infantOrientation === 'supine' ? '#2196f3' :
                                  state.infantOrientation === 'prone' ? '#ff9800' :
                                  state.infantOrientation === 'side' ? '#9c27b0' : '#4caf50',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              />
            </Box>
          )}

          {/* Filming Angle Badge - Top Right */}
          {state.deviceOrientation.filmingAngle !== 'unknown' && (
            <Box
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 20
              }}
            >
              <Chip
                icon={<CameraAlt style={{ color: '#fff', fontSize: '1rem' }} />}
                label={
                  state.deviceOrientation.filmingAngle === 'overhead'
                    ? 'Overhead'
                    : state.deviceOrientation.filmingAngle === 'floor'
                    ? 'Floor Level'
                    : 'Adjusting...'
                }
                style={{
                  backgroundColor:
                    state.deviceOrientation.filmingAngle === 'overhead' ? '#9c27b0' :
                    state.deviceOrientation.filmingAngle === 'floor' ? '#00bcd4' :
                    '#ff9800',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              />
            </Box>
          )}

          {/* MediaPipe Loading Indicator - Top Center */}
          {isMediaPipeLoading && (
            <Box
              style={{
                position: 'absolute',
                top: 60,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                backgroundColor: 'rgba(0,0,0,0.7)',
                padding: '8px 16px',
                borderRadius: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            >
              <CircularProgress size={16} style={{ color: '#fff' }} />
              <Typography variant="caption" style={{ color: '#fff', fontWeight: 500 }}>
                {t('session.loadingai', {ns: 'guidedrecording'}) || 'Initializing AI detection...'}
              </Typography>
            </Box>
          )}

          {/* Guidance Feedback Overlay - Top Center (below badges) */}
          {!isMediaPipeLoading && state.currentGuidance.length > 0 && (
            <Box
              style={{
                position: 'absolute',
                // Move down if either baby orientation badge OR filming angle badge is showing
                top: (state.infantDetected || state.deviceOrientation.filmingAngle !== 'unknown') ? 60 : 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20,
                maxWidth: '90%'
              }}
            >
              <Chip
                label={state.currentGuidance[0].message}
                icon={getFeedbackIcon(state.currentGuidance[0].type)}
                style={{
                  backgroundColor: state.currentGuidance[0].type === 'success' ? '#4caf50' :
                                  state.currentGuidance[0].type === 'error' ? '#f44336' :
                                  state.currentGuidance[0].type === 'warning' ? '#ff9800' : '#2196f3',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  padding: '6px 12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              />
            </Box>
          )}

          {/* Recording Controls Overlay - Bottom */}
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 60%, transparent 100%)',
              padding: '16px 8px 8px 8px',
              zIndex: 20
            }}
          >
            <RecordingControls
              isRecording={state.isRecording}
              recordingDuration={state.recordingDuration}
              onStartRecording={handleStartRecording}
              onStopRecording={handleStopRecording}
              disabled={!state.cameraStream}
            />
          </Box>
        </Box>

        {/* Error Display */}
        {state.error && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {state.error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
